import type { Team, Visit } from '../data/types';
import type { Snapshot } from './scheduling';
import { occupancy } from './scheduling';

export type Pace = 'a-tiempo' | 'lento' | 'rapido' | 'sin-dato';

export interface Progress {
  /** % real según la lista de tareas (no según la ubicación). */
  real: number | null;
  /** % que debería llevar según el tiempo desde el registro de inicio. */
  expected: number | null;
  pace: Pace;
  /** Hora de inicio registrada (punch). Única confirmación de llegada. */
  startedAt: number | null;
}

export function progressOf(v: Visit, now: number): Progress {
  const startedAt = v.assignments.map(a => a.startAt).filter((x): x is number => x != null).sort((a, b) => a - b)[0] ?? null;
  const real = v.checklist ? Math.round((v.checklist.done / v.checklist.total) * 100) : null;
  if (startedAt == null || v.status !== 'inProgress') return { real, expected: null, pace: 'sin-dato', startedAt };
  const expected = Math.min(100, Math.round(((now - startedAt) / (v.end - v.start)) * 100));
  let pace: Pace = 'a-tiempo';
  if (real != null && real <= expected - 10) pace = 'lento';
  else if (real != null && real >= expected + 10) pace = 'rapido';
  return { real, expected, pace, startedAt };
}

/** Fin estimado del trabajo en curso al ritmo actual. */
export function projectedEnd(v: Visit, now: number): number | null {
  const p = progressOf(v, now);
  if (p.real == null || p.startedAt == null || p.real === 0) return null;
  const elapsed = now - p.startedAt;
  return p.startedAt + elapsed / (p.real / 100);
}

export function dayVisits(s: Snapshot, date: string, teamId?: string): Visit[] {
  return s.visits.filter(v => v.date === date && v.status !== 'cancelled' && (!teamId || v.teamId === teamId))
    .sort((a, b) => a.start - b.start);
}

export function todayCounts(s: Snapshot) {
  const vs = dayVisits(s, s.today);
  return {
    total: vs.length,
    done: vs.filter(v => v.status === 'completed').length,
    running: vs.filter(v => v.status === 'inProgress').length,
    risk: vs.filter(v => v.risk).length,
    pending: vs.filter(v => v.status === 'scheduled' && !v.risk).length,
  };
}

export interface TeamHealth {
  team: Team;
  occupancy: number;
  issues: string[];
  /** Avisos que no son problema (p. ej. sin consentimiento de ubicación). */
  notes: string[];
}

export function teamHealth(s: Snapshot): TeamHealth[] {
  return s.teams.map(team => {
    const issues: string[] = [];
    const vs = dayVisits(s, s.today, team.id);
    for (const v of vs) {
      if (v.risk) issues.push(`${v.risk.reason} · ${s.clients.find(c => c.uid === v.clientUid)?.name ?? ''}`);
      const p = progressOf(v, s.now);
      if (p.pace === 'lento') issues.push(`Va lento: ${p.real}% de ${p.expected}% esperado`);
    }
    const occ = occupancy(s, team.id, s.today);
    if (occ >= 0.9) issues.push(`Agenda llena (${Math.round(occ * 100)}%)`);
    const noConsent = team.members.filter(m => !m.locationConsent);
    const notes: string[] = [];
    if (noConsent.length) notes.push(`${noConsent.map(m => m.name.split(' ')[0]).join(', ')}: ubicación desactivada`);
    return { team, occupancy: occ, issues, notes };
  });
}
